import { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowRight, CheckCircle, Compass, Map, Calendar, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  {
    icon: Compass,
    title: "1. Analizamos tu perfil",
    description: "Conocemos tus gustos, experiencias pasadas y qué es lo que realmente te apasiona."
  },
  {
    icon: Map,
    title: "2. Diseño a medida",
    description: "No hay dos guías iguales. Creamos una ruta logística y de experiencias solo para ti."
  },
  {
    icon: Sparkles,
    title: "3. Eliges el formato",
    description: "Tú decides cómo quieres recibir tu aventura: en un elegante PDF interactivo para tu móvil o en una exclusiva edición física encuadernada."
  },
  {
    icon: Calendar,
    title: "4. Disfruta el destino",
    description: "Viaja con la seguridad de tener un plan diseñado por expertos que entienden lo que buscas."
  }
];

export default function GuiasPersonalizadas() {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: '',
    duration: '',
    season: '',
    format: 'digital',
    interests: '',
    previous_experiences: '',
    comments: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: 'Solicitud Guía Personalizada Especializada',
        message: `
          Destino: ${formData.destination}
          Duración del viaje: ${formData.duration}
          Temporada/Mes: ${formData.season}
          Formato preferido: ${formData.format === 'encuadernada' ? 'Encuadernada (Física)' : 'Digital (PDF)'}
          Gustos y aficiones (Museos, compras, relax, etc): ${formData.interests}
          Experiencias anteriores: ${formData.previous_experiences}
          Otros detalles: ${formData.comments}
        `
      });
      toast.success('¡Solicitud de guía enviada! Empezamos a diseñar tu experiencia.');
      setFormData({
        name: '', email: '', phone: '', destination: '',
        duration: '', season: '', format: 'digital', interests: '', previous_experiences: '', comments: ''
      });
    } catch (err) {
      toast.error('Error al enviar. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src="https://images.unsplash.com/photo-1452421822248-d4c2b47f0c81?auto=format&fit=crop&q=80&w=2000"
          alt="Mapa de viaje detallado y diario de ruta"
          className="w-full h-full object-cover shadow-inner"
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
          <div className="container-custom">
            <h1 className="text-4xl lg:text-6xl font-serif text-white mb-4" data-testid="guides-title">
              Guías Personalizadas
            </h1>
            <p className="text-xl text-white/80 max-w-2xl font-serif italic">
              "Porque no hay dos clientes iguales, no debería haber dos viajes iguales."
            </p>
          </div>
        </div>
      </div>

      {/* Intro section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif text-primary mb-8 underline decoration-accent/30 underline-offset-8">Tu viaje, según tus pasiones</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              ¿Eres de los que prefiere perderse en los pasillos de un museo o de los que busca el restaurante más auténtico de la ciudad? ¿Te apasiona ir de compras por avenidas exclusivas o prefieres una ruta de senderismo al amanecer?
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              En <strong>Viajes de Ensueño</strong>, elaboramos guías personalizadas atendiendo a tus gustos, hobbies y experiencias anteriores. Diseñamos un plan que encaje contigo, no un itinerario genérico que podrías encontrar en cualquier sitio.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="section-title">¿Cómo funciona?</h2>
            <p className="section-subtitle mx-auto">
              Un proceso de diseño centrado en ti
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((step, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 text-center transition-transform hover:-translate-y-1"
                data-testid={`step-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mx-auto mb-6">
                  <step.icon className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-lg font-serif font-semibold text-primary mb-3">
                  {step.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-primary">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8 text-center text-white">
            <div className="flex flex-col items-center">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="text-lg font-serif font-semibold mb-2">Diseño exclusivo</h3>
              <p className="text-white/70 text-sm">Cada guía es un documento único creado desde cero</p>
            </div>
            <div className="flex flex-col items-center">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="text-lg font-serif font-semibold mb-2">Filtro emocional</h3>
              <p className="text-white/70 text-sm">Recomendamos lugares que encajan con tu personalidad</p>
            </div>
            <div className="flex flex-col items-center">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="text-lg font-serif font-semibold mb-2">Ahorro de tiempo</h3>
              <p className="text-white/70 text-sm">Nosotros buscamos por ti, tú solo disfrutas</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section bg-white scroll-mt-20" id="formulario">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Comienza tu Guía a Medida</h2>
              <p className="section-subtitle mx-auto">
                Danos unas pinceladas sobre ti y diseñaremos el viaje que realmente disfrutarás
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-slate-100"
              data-testid="personalized-form"
            >
              <div className="grid md:grid-cols-2 gap-10">
                {/* Bloque 1: Contacto */}
                <div className="space-y-6">
                  <h3 className="text-xl font-serif font-bold text-primary flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-sans shrink-0">1</span>
                    ¿Quién eres?
                  </h3>
                  <div className="form-group">
                    <label className="form-label">Nombre completo *</label>
                    <Input
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Tu nombre"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="tu@email.com"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Teléfono *</label>
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+34 600 000 000"
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Bloque 2: Logística */}
                <div className="space-y-6">
                  <h3 className="text-xl font-serif font-bold text-primary flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-sans shrink-0">2</span>
                    Sobre el destino
                  </h3>
                  <div className="form-group">
                    <label className="form-label">¿A qué destino quieres ir? *</label>
                    <Input
                      required
                      value={formData.destination}
                      onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                      placeholder="Ej: Japón, Islandia, Roma..."
                      className="form-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <label className="form-label font-sans font-medium">¿Cuántos días?</label>
                      <Input
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                        placeholder="10 días"
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label font-sans font-medium">Temporada / Mes</label>
                      <Input
                        value={formData.season}
                        onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                        placeholder="Primavera"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Formato de la guía *</label>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, format: 'digital' })}
                        className={`p-4 rounded-xl border-2 transition-all text-sm font-bold flex flex-col items-center gap-2 ${
                          formData.format === 'digital'
                            ? 'border-accent bg-accent/5 text-accent'
                            : 'border-slate-100 hover:border-slate-200 text-slate-500'
                        }`}
                      >
                        <Sparkles className="w-5 h-5" />
                        Guía Digital (PDF)
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, format: 'encuadernada' })}
                        className={`p-4 rounded-xl border-2 transition-all text-sm font-bold flex flex-col items-center gap-2 ${
                          formData.format === 'encuadernada'
                            ? 'border-accent bg-accent/5 text-accent'
                            : 'border-slate-100 hover:border-slate-200 text-slate-500'
                        }`}
                      >
                        <Compass className="w-5 h-5" />
                        Guía Encuadernada (Física)
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bloque 3: Lo emocional/psicológico */}
              <div className="mt-12 pt-10 border-t border-slate-100 space-y-8">
                <h3 className="text-xl font-serif font-bold text-primary flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-accent text-white flex items-center justify-center text-sm font-sans shrink-0">3</span>
                  ¿Cómo quieres vivir el viaje?
                </h3>
                
                <div className="form-group">
                  <label className="form-label text-base">Defínete un poco: ¿Qué te apasiona hacer en un viaje? *</label>
                  <Textarea
                    required
                    value={formData.interests}
                    onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                    placeholder="Ej: Me encantan los museos, descubrir la gastronomía local, ir de compras por zonas exclusivas, la fotografía, el senderismo..."
                    className="min-h-[120px] resize-none text-base p-4"
                  />
                  <p className="mt-2 text-xs text-muted-foreground italic">Esto es lo más importante para que la guía sea realmente tuya.</p>
                </div>

                <div className="form-group">
                  <label className="form-label text-base">Cuéntanos alguna experiencia anterior que te encantara (o que odiaras)</label>
                  <Textarea
                    value={formData.previous_experiences}
                    onChange={(e) => setFormData({ ...formData, previous_experiences: e.target.value })}
                    placeholder="Ej: Me encantó un tour privado gastronómico, pero detesto los autobuses llenos de turistas..."
                    className="min-h-[100px] resize-none text-base p-4"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label text-base">Otros comentarios o peticiones especiales</label>
                  <Textarea
                    value={formData.comments}
                    onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                    placeholder="Viajamos con niños, celebramos un aniversario, movilidad reducida..."
                    className="min-h-[80px] resize-none text-base p-4"
                  />
                </div>

                <div className="flex justify-center pt-8">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="px-12 py-8 bg-accent hover:bg-accent/90 text-white font-bold text-xl rounded-2xl transition-all hover:scale-105 shadow-2xl inline-flex items-center gap-3"
                  >
                    {submitting ? 'Enviando...' : (
                      <>
                        Solicitar mi Guía Personalizada
                        <ArrowRight className="w-6 h-6" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
